import {
  CreationMetric,
  GovernanceEntityWcg,
  Metric,
  MetricGql,
} from '@agilelab/plugin-wb-governance-common';

import {
  parseInfrastructureTemplateIdFromPreprocessing,
  parseUseCaseTemplateIdFromPreprocessing,
} from './utils';
import { CustomAlertApi } from '@agilelab/plugin-wb-platform';

export const toCreationMetric = ({
  governanceEntityWcg,
  alertApi,
  isClone = false,
}: {
  governanceEntityWcg: GovernanceEntityWcg;
  alertApi: CustomAlertApi;
  isClone?: boolean;
}): CreationMetric => {
  let parsedMetadata = {};

  try {
    parsedMetadata = JSON.parse(governanceEntityWcg.additionalMetadata);
  } catch (error) {
    alertApi.post({
      message: `Metric "${governanceEntityWcg.name}" has malformed metadata. Check it with the Platform Team. Metric ID: "${governanceEntityWcg.id}".`,
      severity: 'error',
    });
    parsedMetadata = {};
  }

  return {
    metricId: governanceEntityWcg.id,
    groupId: governanceEntityWcg.groupId,
    status: governanceEntityWcg.status,
    name: governanceEntityWcg.name + (isClone ? ' - Copy' : ''),
    description: governanceEntityWcg.description ?? '',
    context: governanceEntityWcg.context,
    strategy: governanceEntityWcg.strategy,
    engine: governanceEntityWcg.engine,
    resourceType: [governanceEntityWcg.resourceType],
    timing: governanceEntityWcg.timing,
    version: governanceEntityWcg.version,
    selectors: governanceEntityWcg.selectors ?? [],
    tags: governanceEntityWcg.tags,
    environments: [governanceEntityWcg.environment],
    updateTime: new Date(governanceEntityWcg.updateTime),
    cronExpression: governanceEntityWcg.cronExpression ?? undefined,
    interactionType: governanceEntityWcg.interactionType,
    thresholds: (governanceEntityWcg.thresholds?.range ?? []).map(range => ({
      value: range.value.toString(),
      color: range.labelColor,
      label: range.label,
    })),
    domain: {
      min: String(governanceEntityWcg.domain?.lowerBound ?? ''),
      max: String(governanceEntityWcg.domain?.upperBound ?? ''),
    },
    externalUrl: governanceEntityWcg.externalUrl ?? '',
    preprocessing: governanceEntityWcg.preprocessing ?? undefined,
    infrastructureTemplateId: parseInfrastructureTemplateIdFromPreprocessing(
      governanceEntityWcg.preprocessing,
    ),
    useCaseTemplateId: parseUseCaseTemplateIdFromPreprocessing(
      governanceEntityWcg.preprocessing,
    ),
    additionalMetadata: parsedMetadata,
    trigger: governanceEntityWcg.trigger,
    expression: governanceEntityWcg.thresholds?.expression,
  };
};

export const toGovernanceMetric = (gqlMetric: MetricGql): Metric => {
  let parsedMetadata = {};

  try {
    parsedMetadata = JSON.parse(gqlMetric?.additional_metadata);
  } catch (error) {
    parsedMetadata = {};
  }

  const domain = gqlMetric?.content?.domain ?? undefined;

  return {
    metricId: gqlMetric?.governance_entity_id,
    groupId: gqlMetric?.group_id,
    status: gqlMetric?.status,
    name: gqlMetric?.name,
    description: gqlMetric?.description || '',
    context: gqlMetric?.context,
    strategy: gqlMetric?.strategy,
    resourceType: gqlMetric?.resource_type?.name,
    timing: gqlMetric?.timing,
    version: gqlMetric?.version,
    selectors:
      gqlMetric?.selector?.map(s => ({ path: s.jsonPath, ...s })) || [],
    tags: gqlMetric?.governance_entity_tags?.map(t => t.tag.name),
    environments:
      gqlMetric?.governance_entity_environments?.map(e => e.environment.name) ||
      [],
    updateTime: new Date(gqlMetric?.update_time),
    cronExpression: gqlMetric?.cron_expression || undefined,
    interactionType: gqlMetric?.interaction_type,
    thresholds: (gqlMetric?.content?.thresholds?.range || [])?.map(t => ({
      ...t,
      value: String(t.value),
      color: t.labelColor,
    })),
    domain: {
      min:
        domain?.lowerBound !== null && domain?.lowerBound !== undefined
          ? String(gqlMetric?.content.domain?.lowerBound)
          : '',
      max:
        domain?.upperBound !== null && domain?.upperBound !== undefined
          ? String(gqlMetric?.content.domain?.upperBound)
          : '',
    },
    externalUrl: gqlMetric?.content?.externalUrl ?? '',
    infrastructureTemplateId: parseInfrastructureTemplateIdFromPreprocessing(
      gqlMetric?.preprocessing,
    ),
    engine: gqlMetric?.engine,
    useCaseTemplateId: parseUseCaseTemplateIdFromPreprocessing(
      gqlMetric?.preprocessing,
    ),
    preprocessing: gqlMetric?.preprocessing || undefined,
    additionalMetadata: parsedMetadata,
    trigger: gqlMetric?.trigger,
    expression: gqlMetric?.content?.thresholds?.expression,
  };
};

export const toGovernanceMetricFromWcg = (
  governanceEntityWcg: GovernanceEntityWcg,
  alertApi: CustomAlertApi,
): Metric => {
  let parsedMetadata = {};

  try {
    parsedMetadata = JSON.parse(governanceEntityWcg.additionalMetadata);
  } catch (error) {
    alertApi.post({
      message: `Metric "${governanceEntityWcg.name}" has malformed metadata. Check it with the Platform Team. Metric ID: "${governanceEntityWcg.id}".`,
      severity: 'error',
    });
    parsedMetadata = {};
  }

  return {
    metricId: governanceEntityWcg.id,
    groupId: governanceEntityWcg.groupId,
    status: governanceEntityWcg.status,
    name: governanceEntityWcg.name,
    description: governanceEntityWcg.description ?? '',
    context: governanceEntityWcg.context,
    strategy: governanceEntityWcg.strategy,
    resourceType: governanceEntityWcg.resourceType,
    timing: governanceEntityWcg.timing,
    version: governanceEntityWcg.version,
    selectors: governanceEntityWcg.selectors ?? [],
    tags: governanceEntityWcg.tags,
    environments: [governanceEntityWcg.environment],
    updateTime: new Date(governanceEntityWcg.updateTime),
    cronExpression: governanceEntityWcg.cronExpression ?? undefined,
    interactionType: governanceEntityWcg.interactionType,
    externalUrl: governanceEntityWcg.externalUrl ?? '',
    thresholds:
      governanceEntityWcg.thresholds?.range?.map(range => ({
        color: range.labelColor,
        label: range.label,
        value: range.value.toString(),
      })) ?? [],
    engine: governanceEntityWcg.engine,
    infrastructureTemplateId: parseInfrastructureTemplateIdFromPreprocessing(
      governanceEntityWcg.preprocessing,
    ),
    useCaseTemplateId: parseUseCaseTemplateIdFromPreprocessing(
      governanceEntityWcg.preprocessing,
    ),
    additionalMetadata: parsedMetadata,
    preprocessing: governanceEntityWcg.preprocessing ?? undefined,
    trigger: governanceEntityWcg.trigger,
    domain: {
      min: String(governanceEntityWcg.domain?.lowerBound) ?? '',
      max: String(governanceEntityWcg.domain?.upperBound) ?? '',
    },
    expression: governanceEntityWcg.thresholds?.expression,
  };
};
